/**
 * Comment composer — pops up over the player with the captured anchor (slide + timestamp).
 *
 * Captures slide context at open time so the reviewer can keep typing even if the player
 * accidentally advances (it shouldn't — we pause on open, but we want to be safe).
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { AutoResizeTextarea } from '../../ui/auto-resize-textarea';
import { X } from 'lucide-react';
import { useReviewSession } from './useReviewSession';
import { formatTimecode } from './format';

interface Anchor {
  slideId: string;
  slideIndex: number;
  slideTitle: string;
  timestamp: number;
}

interface Props {
  open: boolean;
  anchor: Anchor | null;
  onClose: () => void;
}

export function CommentComposer({ open, anchor, onClose }: Props) {
  const addDraft = useReviewSession((s) => s.addDraft);
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset whenever the composer opens with a fresh anchor
  useEffect(() => {
    if (open) {
      setText('');
      // focus on next tick after the animation kicks off
      const id = window.setTimeout(() => textareaRef.current?.focus(), 80);
      return () => window.clearTimeout(id);
    }
  }, [open, anchor?.slideId, anchor?.timestamp]);

  // Esc to cancel, Cmd/Ctrl+Enter to save
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        save();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const save = () => {
    if (!anchor) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    addDraft({
      slideId: anchor.slideId,
      slideIndex: anchor.slideIndex,
      slideTitle: anchor.slideTitle,
      timestamp: anchor.timestamp,
      text: trimmed,
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && anchor && (
        <motion.div
          key="composer"
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.15 }}
          className="fixed bottom-24 right-6 z-40 w-[380px] max-w-[calc(100vw-32px)] bg-zinc-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl text-white"
          role="dialog"
          aria-label="Add review comment"
        >
          <div className="flex items-start justify-between px-4 pt-3 pb-2 border-b border-white/10">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-white/50 font-medium">Comment on</div>
              <div className="text-sm font-medium truncate">
                Slide {anchor.slideIndex + 1}
                {anchor.slideTitle ? ` · ${anchor.slideTitle}` : ''}
              </div>
              <div className="text-xs text-white/60 mt-0.5">at {formatTimecode(anchor.timestamp)}</div>
            </div>
            <button
              onClick={onClose}
              className="text-white/50 hover:text-white p-1 -mr-1 rounded hover:bg-white/10"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-3">
            <AutoResizeTextarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share what should change at this moment…"
              maxLength={2000}
              minRows={3}
              maxRows={8}
              className="w-full bg-transparent border border-white/10 focus:border-white/30 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40 resize-none focus:outline-none"
            />
          </div>
          <div className="flex items-center justify-between gap-2 px-3 pb-3">
            <span className="text-xs text-white/40">
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-mono">⌘↵</kbd> to save
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} className="text-white/70 hover:text-white hover:bg-white/10">
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={!text.trim()}>
                Save comment
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
